import type { WatchSource } from 'vue';
import { reactive, toRaw } from 'vue';

import type {
  OperationDeleteDTO,
  OperationMemberRemoveData,
  OperationTrackerDetailsEventDTO,
  OperationUserDTO,
  UserDetailsUpdateEventDTO,
} from '@nora-monorepo/nora-api-client';
import { ConsentScopeTypeEnum } from '@nora-monorepo/nora-api-client';
import { ensureValidDate, isNonEmptyString, isNonZero } from '@nora-monorepo/shared';
import type { PositionResponse } from '@nora-monorepo/track-api-web-client';
import { TrackingUtilities, useGenericUserStore } from '@nora-monorepo/ui-shared';

import { MapUtils } from '#nora-ui/map/map/MapUtils';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { NoraStateManager } from '#nora-ui/map/map/state-managers/NoraStateManager';
import type { Persistable, TrackStateSnapshot } from '#nora-ui/map/types/persistence.types';
import type { LastKnownTrack, TrackProperties } from '#nora-ui/map/types/track.types';
import { TrackVisibilityState } from '#nora-ui/map/types/track.types';
import { useOperationStore } from '#nora-ui/stores/operation-store';
import { useTracksStore } from '#nora-ui/stores/tracks-store';

export class TrackStateManager extends NoraStateManager implements Persistable<TrackStateSnapshot> {
  private operationStore = useOperationStore();
  private userStore = useGenericUserStore();

  /**
   * Track visibility per producer, global to the map instance.
   */
  private visibleTracks = reactive(new Map<string, TrackVisibilityState>());

  private tracksStore = useTracksStore();
  private unsubscribe?: () => void;
  private staleCheckInterval?: ReturnType<typeof setInterval>;

  private STALE_LOCATION_INTERVAL_CHECK_IN_MS: number = 60 * 1000; // every minute

  private lastKnownTrackPositions: {
    [userId: string]: LastKnownTrack;
  } = {};

  areAllOperationTracksVisible(operationId: string): boolean {
    const producerIds = [
      ...this.map.ref.operationUserIds(operationId),
      ...this.map.ref.operationTrackerIds(operationId),
    ];

    if (producerIds.length === 0) {
      return false;
    }

    return producerIds.every(
      producerId => this.getTrackVisibilityState(producerId) !== TrackVisibilityState.Hidden,
    );
  }

  private staleLocationCheck(): void {
    const userLocationsToRemove = this.operationStore.getStaleOperationUserLocationIds();
    const trackerLocationsToRemove = this.operationStore.getStaleOperationTrackerLocationIds();

    if (!userLocationsToRemove?.length && !trackerLocationsToRemove?.length) {
      return;
    }

    for (const userId of userLocationsToRemove) {
      this.removeTrack(userId);
    }

    for (const trackerId of trackerLocationsToRemove) {
      this.removeTrack(trackerId);
    }
  }

  private startStaleLocationTimer(): void {
    this.staleCheckInterval = setInterval(() => {
      this.staleLocationCheck();
    }, this.STALE_LOCATION_INTERVAL_CHECK_IN_MS);
  }

  constructor(private readonly map: NoraMap) {
    super();

    this.setupTrackListener();
    this.staleLocationCheck();
    this.startStaleLocationTimer();
  }

  private setupTrackListener(): void {
    this.unsubscribe = this.tracksStore.addListener((track: PositionResponse) => {
      this.handleIncomingTrack(track);
    });
  }

  private handleIncomingTrack(position: PositionResponse): void {
    if (TrackingUtilities.isUserPosition(position)) {
      this.handleIncomingUserTrack(position);
    }
    if (TrackingUtilities.isTrackerPosition(position)) {
      this.handleIncomingTrackerTrack(position);
    }
  }

  private handleIncomingUserTrack(position: PositionResponse): void {
    const producerId = position.producerId;
    const properties = this.getUserPropertiesForTrack(position);
    const isCurrentUser = this.userStore.isCurrentUser(producerId);

    if (isCurrentUser) {
      this.renderTrack(position, properties);

      return;
    }

    const visibleOperationIds = this.map.ref.visibleOperationIds;

    const isUserInVisibleOperation = visibleOperationIds.some(
      operationId => this.operationStore.isUserInOperation(producerId, operationId),
    );

    if (!isUserInVisibleOperation) {
      return;
    }

    const isUserSharingLocation = visibleOperationIds.some(
      operationId => this.operationStore.isUserSharingLocationInOperation(producerId, operationId),
    );

    if (!isUserSharingLocation) {
      return;
    }

    const userLocationIsStale = this.locationIsStale(position, this.operationStore.staleUserLocationTime);

    if (userLocationIsStale) {
      return;
    }

    this.renderTrack(position, properties);
  }

  private renderTrack(position: PositionResponse, properties: TrackProperties): void {
    const producerId = position.producerId;

    this.lastKnownTrackPositions[producerId] = {
      position,
      properties,
    };

    this.map.layers.track.handleIncomingTrack(position, properties);

    const hasHeading = position.heading !== null && position.heading !== undefined;
    const isCurrentUser = this.userStore.isCurrentUser(producerId);

    if (isCurrentUser && this.map.ref.isInHeadingUpMode && hasHeading) {
      const mapRotationInRadians = MapUtils.userHeadingDegreesToMapRotationRadians(position.heading);
      this.map.setRotation(mapRotationInRadians);
    }
  }

  private handleIncomingTrackerTrack(position: PositionResponse): void {
    const producerId = position.producerId;

    const isTrackerInVisibleOperation = this.map.ref.visibleOperationIds.some(
      operationId => this.operationStore.isTrackerInOperation(producerId, operationId),
    );

    if (!isTrackerInVisibleOperation) {
      return;
    }
    const trackerLocationIsStale = this.locationIsStale(position, this.operationStore.staleTrackerLocationTime);

    if (trackerLocationIsStale) {
      return;
    }

    const properties = this.getTrackerPropertiesForTrack(position);

    this.renderTrack(position, properties);
  }

  /**
   * Returns true if the given position response is older than given permitted `thresholdTime`.
   * @param position
   * @param thresholdTime - a time in milliseconds representing how old a track can be before it's considered stale. If 0, no track will be considered stale based on time.
   * @private
   * @returns a boolean representing whether the position date is stale or not
   */
  private locationIsStale(position: PositionResponse, thresholdTime: number): boolean {
    const lastTrackTime = ensureValidDate(position.serverTimestamp)?.getTime();

    if (!isNonZero(lastTrackTime)) {
      return true;
    }

    if (thresholdTime !== 0) {
      const lastAcceptableTrackTime = Date.now() - thresholdTime;
      if (lastTrackTime <= lastAcceptableTrackTime) {
        return true;
      }
    }

    return false;
  }

  override cleanup(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
    if (this.staleCheckInterval !== undefined) {
      clearInterval(this.staleCheckInterval);
      this.staleCheckInterval = undefined;
    }
  }

  /**
   * Load track state
   */
  async load(): Promise<void> {
    this.unload();
    this.staleLocationCheck();
    await this.initializeTracks();
  }

  /**
   * Loads the tracks of a single operation alongside the operations already visible.
   */
  async loadOperation(operationId: string): Promise<void> {
    this.staleLocationCheck();
    this.initialiseOperationTracksForOperation(operationId);
  }

  /**
   * Releases the tracks of a single operation by sweeping the rendered tracks, removing
   * every producer no longer justified by another visible operation. Sweeping rendered
   * tracks (instead of the operation's members) works even when the operation-store
   * record is already gone.
   */
  releaseOperation(operationId: string): void {
    for (const producerId of Object.keys(this.lastKnownTrackPositions)) {
      const staysRendered = this.userStore.isCurrentUser(producerId)
        || this.isProducerVisibleElsewhere(producerId, operationId);

      if (!staysRendered) {
        this.removeTrack(producerId);
      }
    }
  }

  /**
   * Whether a producer belongs to a visible operation other than the given one.
   */
  private isProducerVisibleElsewhere(producerId: string, excludedOperationId: string): boolean {
    return this.map.ref.visibleOperationIds
      .filter(operationId => operationId !== excludedOperationId)
      .some(operationId =>
        this.operationStore.isUserInOperation(producerId, operationId)
        || this.operationStore.isTrackerInOperation(producerId, operationId),
      );
  }

  /**
   * Initialize user and tracker locations with last last track
   */
  private async initializeTracks(): Promise<void> {
    this.initialiseCurrentUserTrack();
    this.initialiseOperationTracks();
  }

  private initialiseCurrentUserTrack(): void {
    const user = this.userStore.user;
    if (user && user.lastTrack) {
      const trackToPositionResponse = TrackingUtilities.trackDTOToPositionResponse(user.lastTrack);
      this.handleIncomingTrack(trackToPositionResponse);
    }
  }

  private initialiseOperationTracks(): void {
    for (const operationId of this.map.ref.visibleOperationIds) {
      this.initialiseOperationTracksForOperation(operationId);
    }
  }

  private initialiseOperationTracksForOperation(operationId: string): void {
    const operation = this.operationStore.getOperation(operationId);

    if (!operation) {
      return;
    }

    for (const { user } of operation.users ?? []) {
      const rawUser = toRaw(user);

      if (!rawUser.lastTrack) {
        continue;
      }

      const trackToPositionResponse = TrackingUtilities.trackDTOToPositionResponse(rawUser.lastTrack);

      this.handleIncomingTrack(trackToPositionResponse);
    }

    for (const tracker of operation.trackers ?? []) {
      const rawTracker = toRaw(tracker);

      if (!rawTracker.lastTrack) {
        continue;
      }

      const trackToPositionResponse = TrackingUtilities.trackDTOToPositionResponse(rawTracker.lastTrack);

      this.handleIncomingTrack(trackToPositionResponse);
    }
  }

  /**
   * Unload/clear all track state.
   */
  unload(): void {
    for (const producerId of Object.keys(this.lastKnownTrackPositions)) {
      this.map.layers.track.clearUserTrack(producerId);
    }
    this.lastKnownTrackPositions = {};
    this.visibleTracks.clear();
  }

  setTrackVisibility(producerId: string, state: TrackVisibilityState): void {
    this.visibleTracks.set(producerId, state);

    this.map.layers.track.setTrackVisibility(producerId, state);
  }

  isTrackVisible(producerId: string): boolean {
    return this.getTrackVisibilityState(producerId) !== TrackVisibilityState.Hidden;
  }

  getTrackVisibilityState(producerId: string): TrackVisibilityState {
    return this.visibleTracks.get(producerId) ?? TrackVisibilityState.Visible;
  }

  handleUserLocationScopeUpdate(changedOperationUser: OperationUserDTO): void {
    if (!this.map.ref.isOperationVisible(changedOperationUser.operationId)) {
      return;
    }

    if (this.userStore.isCurrentUser(changedOperationUser.user.id)) {
      return;
    }

    const hasLocationScope = changedOperationUser.consent.scopes.some(scope => scope.scope === ConsentScopeTypeEnum.LOCATION);

    if (!hasLocationScope) {
      this.removeTrackIfNotSharedElsewhere(changedOperationUser.user.id, changedOperationUser.operationId);
      return;
    }

    const lastUserTrack = changedOperationUser.user.lastTrack;
    if (!lastUserTrack) {
      return;
    }

    const trackToPositionResponse = TrackingUtilities.trackDTOToPositionResponse(lastUserTrack);
    this.handleIncomingTrack(trackToPositionResponse);
  }

  handleUserLocationClear(userId: string, operationId: string): void {
    if (!this.map.ref.isOperationVisible(operationId) || this.userStore.isCurrentUser(userId)) {
      return;
    }

    this.removeTrackIfNotSharedElsewhere(userId, operationId);
  }

  private removeTrackIfNotSharedElsewhere(userId: string, excludedOperationId: string): void {
    const sharesLocationElsewhere = this.map.ref.visibleOperationIds
      .some(operationId =>
        operationId !== excludedOperationId
        && this.operationStore.isUserInOperation(userId, operationId)
        && this.operationStore.isUserSharingLocationInOperation(userId, operationId),
      );

    if (!sharesLocationElsewhere) {
      this.removeTrack(userId);
    }
  }

  handleOperationRemoveMemberEvent(operationRemovedUser: OperationMemberRemoveData): void {
    const { operationId, userId } = operationRemovedUser;

    if (!this.map.ref.hasOperation(operationId)) {
      return;
    }

    // Losing membership removes the whole operation; releaseOperation keeps the current
    // user's own track.
    if (this.userStore.isCurrentUser(userId)) {
      this.map.removeOperation(operationId);

      return;
    }

    if (!this.isProducerVisibleElsewhere(userId, operationId)) {
      this.removeTrack(userId);
    }
  }

  handleOperationDeleteEvent(deletedOperationData: OperationDeleteDTO): void {
    if (!this.map.ref.hasOperation(deletedOperationData.id)) {
      return;
    }

    this.map.removeOperation(deletedOperationData.id);
  }

  booleanToVisibilityState(visible: boolean): TrackVisibilityState {
    return visible ? TrackVisibilityState.Visible : TrackVisibilityState.Hidden;
  }

  clearAllVisibility(): void {
    this.visibleTracks.clear();
  }

  removeTrack(producerId: string): void {
    delete this.lastKnownTrackPositions[producerId];
    this.map.layers.track.clearUserTrack(producerId);
  }

  setAllOperationTracksVisibility(operationId: string, visible: boolean): void {
    const state = this.booleanToVisibilityState(visible);

    for (const userId of this.map.ref.operationUserIds(operationId)) {
      this.setTrackVisibility(userId, state);
    }

    for (const trackerId of this.map.ref.operationTrackerIds(operationId)) {
      this.setTrackVisibility(trackerId, state);
    }
  }

  /**
   * Gets the last known track position for a "track producer" by their ID.
   */
  getLastKnownTrackPositionForProducerId(producerId: string): PositionResponse | undefined {
    return this.getLastKnownTrackForProducerId(producerId)?.position;
  }

  /**
   * Gets the last known track for a "track producer" by their ID.
   */
  getLastKnownTrackForProducerId(producerId: string): LastKnownTrack | undefined {
    return this.lastKnownTrackPositions[producerId];
  }

  /**
   * Gets all last known tracks.
   * Used by TrackLayerManager to render tracks from cached positions.
   */
  getAllLastKnownTracks(): Record<string, LastKnownTrack> {
    return this.lastKnownTrackPositions;
  }

  /**
   * Gets the user properties for a track based on its position.
   */
  private getUserPropertiesForTrack(position: PositionResponse): TrackProperties {
    const userMetadata = this.operationStore.operationUserMetadata.get(position.producerId);
    const sidc = position.properties['sidc'];

    return {
      callSign: userMetadata?.userName,
      symbolId: userMetadata?.symbol?.id,
      symbolPackId: userMetadata?.symbol?.packId,
      sidc: isNonEmptyString(sidc) ? sidc : undefined,
    };
  }

  /**
   * Gets the tracker properties for a track based on its position.
   */
  private getTrackerPropertiesForTrack(position: PositionResponse): TrackProperties {
    const trackerMetadata = this.operationStore.operationTrackerMetadata.get(position.producerId);
    const sidc = position.properties['sidc'];

    return {
      callSign: trackerMetadata?.trackerName,
      symbolId: trackerMetadata?.symbol?.id,
      symbolPackId: trackerMetadata?.symbol?.packId,
      sidc: isNonEmptyString(sidc) ? sidc : undefined,
    };
  }

  setTrackerPropertiesForTrack(trackerData: OperationTrackerDetailsEventDTO): void {
    const lastKnownTrack = this.getLastKnownTrackForProducerId(trackerData.id);
    if (lastKnownTrack) {
      const updatedProperties = this.getTrackerPropertiesForTrack(lastKnownTrack.position);
      this.map.layers.track.handleIncomingTrack(lastKnownTrack.position, updatedProperties);
    }
  }

  setUserPropertiesForTrack(userData: UserDetailsUpdateEventDTO): void {
    const lastKnownTrack = this.getLastKnownTrackForProducerId(userData.userId);
    if (lastKnownTrack) {
      const updatedProperties = this.getUserPropertiesForTrack(lastKnownTrack.position);
      this.map.layers.track.handleIncomingTrack(lastKnownTrack.position, updatedProperties);
    }
  }

  /**
   * Serialize the track state to a plain object for persistence.
   */
  serialize(): TrackStateSnapshot {
    return {
      lastKnownPositions: { ...this.lastKnownTrackPositions },
      visibility: Array.from(this.visibleTracks.entries()),
    };
  }

  /**
   * Restore track state from a persisted snapshot.
   */
  deserialize(snapshot: TrackStateSnapshot): void {
    // Restore last known positions
    this.lastKnownTrackPositions = { ...snapshot.lastKnownPositions };

    // Restore visibility settings
    this.visibleTracks.clear();
    for (const [producerId, state] of snapshot.visibility) {
      this.visibleTracks.set(producerId, state);
    }

    // Re-render tracks on the layer from restored positions
    for (const [producerId, track] of Object.entries(this.lastKnownTrackPositions)) {
      if (this.isTrackVisible(producerId)) {
        this.map.layers.track.handleIncomingTrack(track.position, track.properties ?? {});
      }
    }
  }

  /**
   * Returns a watch source for triggering persistence when state changes.
   */
  getWatchSource(): WatchSource {
    // Positions are a plain object, so watching them created no dependency, only a costly deep
    // walk. Visibility drives persistence; positions still ride along whenever it fires.
    return () => Array.from(this.visibleTracks.entries());
  }
}
